'use client';

import {
  NavItemLevelOneProps,
  SiteHeaderProps,
  SiteHeader as V3SiteHeader,
} from '@stadionhq-v3/base-ui';
import classnames from 'classnames';
import { Suspense } from 'react';
import { sanitiseLinkPropsForHTMLAnchor } from '@utils/paths/path.utils';
import styles from './siteHeader.module.scss';

export const pointerTypes = ['mouse', 'pen', 'touch'] as const;

export type PointerType = (typeof pointerTypes)[number];

export const SiteHeader = ({
  /* props */
  classNames,
  navItems,
  closeButton,
  megaMenuTrigger,
  megaMenuBackButton,
  moreMenuTrigger,
  moreMenuItems,
  moreMenuProps,
  ssoNav,
  currentUrl,
  ...props
}: SiteHeaderProps) => {
  const classes = classnames(
    [styles.siteHeaderTokens, styles.siteHeader],
    classNames
  );

  return (
    <Suspense>
      <V3SiteHeader
        classNames={[classes]}
        additionalTriggers={[ssoNav]}
        closeButton={
          closeButton && {
            ...closeButton,
          }
        }
        megaMenuTrigger={
          megaMenuTrigger && {
            ...megaMenuTrigger,
            variant: 'primary',
            isNeutral: true,
            classNames: [classnames(styles.megaMenuTrigger)],
          }
        }
        searchOverlayTrigger={
          props.searchOverlayTrigger && {
            ...props.searchOverlayTrigger,
            classNames: [classnames(styles.searchOverlayTrigger)],
          }
        }
        megaMenuBackButton={
          megaMenuBackButton && {
            ...megaMenuBackButton,
            classNames: [
              classnames(
                styles.small,
                styles.button,
                styles.megaMenuBackButton
              ),
            ],
          }
        }
        moreMenuTrigger={
          moreMenuTrigger && {
            ...moreMenuTrigger,
            classNames: [
              classnames(
                moreMenuTrigger.classNames,
                styles.navItemLevelOne,
                styles.navItemLevelOneTokens,
                styles.navItemLevelOneEmphasis
              ),
            ],
          }
        }
        moreMenuItems={moreMenuItems?.map((moreMenuItem) => ({
          ...moreMenuItem,
          classNames: [
            classnames(
              moreMenuItem.classNames,
              styles.navItemLevelFour,
              styles.navItemLevelFourTokens,
              {
                [`${styles.navItemLevelFourSelected}`]:
                  moreMenuItem.isSelected || moreMenuItem.url === currentUrl,
              }
            ),
          ],
          secondaryLinks: moreMenuItem.secondaryLinks?.map(
            (moreMenuItemTwo) => ({
              ...moreMenuItemTwo,
              iconAfter: undefined,
              classNames: [
                classnames(
                  moreMenuItemTwo.classNames,
                  styles.navItemLevelFour,
                  styles.navItemLevelFourTokens,
                  {
                    [`${styles.navItemLevelFourSelected}`]:
                      moreMenuItemTwo.isSelected,
                  }
                ),
              ],
            })
          ),
        }))}
        megaMenuProps={{
          classNames: [classnames(styles.megaMenu)],
        }}
        flyoutMenuProps={{
          classNames: [classnames(styles.flyoutMenu)],
        }}
        {...props}
        moreMenuProps={{
          ...moreMenuProps,
          classNames: [classnames(styles.moreMenu)],
          // 'link' renders each section title via NavItemLevelFour (an
          // anchor using the nav item's resolved url) instead of a plain h6
          // heading, so the section's own internal link is navigable.
          sectionTitleType: 'link',
          sectionTitleClassNames: [classnames(styles.moreSectionTitle)],
        }}
        navItems={navItems.map((navItem) => ({
          ...sanitiseLinkPropsForHTMLAnchor<
            NavItemLevelOneProps & { internalLinkData?: string }
          >(navItem),
          classNames: [
            classnames(
              navItem.classNames,
              styles.navItemLevelOne,
              styles.navItemLevelOneTokens,
              {
                [`${styles.navItemLevelOneSelected}`]:
                  navItem.isSelected ||
                  navItem.url === currentUrl ||
                  currentUrl?.includes(navItem.url || ''),
                [`${styles.navItemLevelOneEmphasis}`]: navItem.onDark,
              }
            ),
          ],
          secondaryLinks: navItem.secondaryLinks?.map((navItemTwo) => ({
            ...navItemTwo,
            iconAfter:
              Boolean(navItemTwo.secondaryLinks?.length) &&
              navItemTwo.iconAfter,
            classNames: [
              classnames(
                navItemTwo.classNames,
                styles.navItemLevelTwo,
                styles.navItemLevelTwoTokens,
                {
                  [`${styles.navItemLevelTwoSelected}`]:
                    navItemTwo.isSelected ||
                    navItemTwo.url === currentUrl ||
                    currentUrl?.includes(navItemTwo.url || ''),
                }
              ),
            ],
            secondaryLinks: navItemTwo.secondaryLinks?.map((navItemThree) => ({
              ...navItemThree,
              classNames: [
                classnames(
                  navItemThree.classNames,
                  styles.navItemLevelThree,
                  styles.navItemLevelThreeTokens,
                  {
                    [`${styles.navItemLevelThreeSelected}`]:
                      navItemThree.isSelected ||
                      navItemThree.url === currentUrl ||
                      currentUrl?.includes(navItemThree.url || ''),
                  }
                ),
              ],
            })),
          })),
        }))}
      />
    </Suspense>
  );
};
